// bluaIQ — App Router & Layout
// Main navigation shell with sidebar + routes for all modules
// Supports ?lang=en / ?lang=es for marketing links (ads, emails)

import { apiFetch } from '../utils/api'
import { useState, useEffect } from 'react'
import { BrowserRouter, Routes, Route, NavLink, Navigate, useParams, useSearchParams, useLocation } from 'react-router-dom'
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { SignIn, SignUp, useAuth, useUser, UserButton } from '@clerk/clerk-react'
import * as Sentry from '@sentry/react'
import {
  LayoutDashboard, FileText, DollarSign, Hammer, BarChart3,
  Shield, ScrollText, CreditCard, Users, Camera, Crown, FileCheck2,
  Menu, X, Globe, ChevronLeft, ChevronDown, Receipt, Mail, CalendarDays, FolderOpen, Sparkles, Truck, Brain,
  Settings, MessageCircle, FileSpreadsheet, GanttChart, PenTool,
  Palette, Sparkles as SparklesIcon, ClipboardList, Link2,
  // Icons added by the 7-group sidebar regrouping (PR — Sidebar 7-group
  // builder workflow) for the four new placeholder entries + Change
  // Orders (which now becomes visible under Control del Proyecto).
  Layers, BookOpen, Stamp, CheckSquare, RefreshCw, PackageCheck,
  ListChecks
} from 'lucide-react'

// ── Page imports ──────────────────────────────────
import LandingPage from './LandingPage'
import ProjectList from './ProjectList'
import Dashboard from './Dashboard'
import TakeoffModule from './TakeoffModule'
import SuppliersModule from './SuppliersModule'
import QuotesModule from './QuotesModule'
import BidsModule from './BidsModule'
import BudgetModule from './BudgetModule'
import InsuranceModule from './InsuranceModule'
import ContractsModule from './ContractsModule'
import PaymentsModule from './PaymentsModule'
import ContactsModule from './ContactsModule'
import PhotosModule from './PhotosModule'
import PermitsModule from './PermitsModule'
import SubscriptionModule from './SubscriptionModule'
import ReceiptsModule from './ReceiptsModule'
import EmailsModule from './EmailsModule'
import CalendarModule from './CalendarModule'
import CalendarSyncModule from './CalendarSyncModule'
import DocumentsModule from './DocumentsModule'
import AccountBlockedScreen from './AccountBlockedScreen'
import FinishSelectionsWizard from './FinishSelectionsWizard'
import MaterialOrderSchedule from './MaterialOrderSchedule'
import InsightsModule from './InsightsModule'
import ContractSignPage from './ContractSignPage'
import ProjectChatModule from './ProjectChatModule'
import AIABillingModule from './AIABillingModule'
import GanttModule from './GanttModule'
import DrawingMarkup from './DrawingMarkup'
import SelectionBoardModule from './SelectionBoardModule'
import DailyLogModule from './DailyLogModule'
import ChangeOrderModule from './ChangeOrderModule'
import PunchListModule from './PunchListModule'
import RFIModule from './RFIModule'
import IntegrationsModule from './IntegrationsModule'
import TeamModule, { InviteAcceptPage } from './TeamModule'
import ComingSoonPlaceholder from './ComingSoonPlaceholder'
import ProFeatureGate from './ProFeatureGate'
import { ProBadge } from './ProFeatureGate'
import { PWAInstallPrompt } from './PWAInstallPrompt'
import ErrorBoundary from './ErrorBoundary'
import PaymentWarningBanner from './PaymentWarningBanner'
import ReadOnlyBanner from './ReadOnlyBanner'
import { isDemoMode, isDemoProject, DEMO_PROJECTS } from '../utils/demoData'
import SavedProjectOnlyNotice from './SavedProjectOnlyNotice'
import ProjectContextHeader from './ProjectContextHeader'
import DiagnosticTakeoffModule from './takeoffDiagnostic/DiagnosticTakeoffModule'
import AiDraftTakeoffShell from './takeoffAiDraft/AiDraftTakeoffShell'
import ProjectMaterialListDemoShell from './projectMaterialList/ProjectMaterialListDemoShell'
import TakeoffReadonlyDisplayShell from './takeoffReadonlyDisplay/TakeoffReadonlyDisplayShell'
import CertifiedPurchaseCandidateView from './CertifiedPurchaseCandidateView'
import CertifiedQuoteDraftView from './CertifiedQuoteDraftView'
import CertifiedSelectionDraftView from './CertifiedSelectionDraftView'
import { isDiagnosticV3VisibleToClient } from '../takeoff-diagnostic-v3/clientFlags'
import { isTakeoffOwnerAccount } from '../takeoff-diagnostic-v3/takeoffOwnerGate'
import { isTakeoffAvailable } from '../takeoff-diagnostic-v3/takeoffAvailability'
import { TakeoffAvailabilityContext } from '../takeoff-diagnostic-v3/TakeoffAvailabilityContext'
import { useSubscriptionPlan } from '../hooks/useSubscriptionPlan'

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, retry: 1 } }
})

interface AuthenticatedMobileHeaderProps {
  isDemo: boolean
  isEs: boolean
  onMenu: () => void
  onToggleLang: () => void
}

export function AuthenticatedMobileHeader({
  isDemo,
  isEs,
  onMenu,
  onToggleLang,
}: AuthenticatedMobileHeaderProps) {
  return (
    <div className={`lg:hidden fixed inset-x-0 z-50 bg-white border-b border-[#ECEAE4] px-4 py-3 flex items-center justify-between ${isDemo ? 'top-[32px]' : 'top-0'}`}
      data-testid="authenticated-mobile-header"
    >
      <div className="flex items-center gap-2">
        <NavLink
          to="/projects"
          aria-label={isEs ? 'Volver a proyectos' : 'Back to projects'}
          className="p-1 text-[#9E9890] hover:text-[#1C1A17]"
        >
          <ChevronLeft size={20} />
        </NavLink>
        <button
          type="button"
          onClick={onMenu}
          aria-label={isEs ? 'Abrir navegación' : 'Open navigation'}
          className="p-1"
        >
          <Menu size={20} className="text-[#1C1A17]" />
        </button>
        <img
          src="/natami_logo.png"
          alt="NATAMI"
          data-testid="mobile-header-logo"
          className="h-[20px] w-auto max-w-[110px] object-contain"
        />
      </div>
      <button
        type="button"
        onClick={onToggleLang}
        aria-label={isEs ? 'Cambiar a inglés' : 'Switch to Spanish'}
        className="flex items-center gap-1.5 text-[12px] font-[700] text-[#9E9890]"
      >
        <Globe size={14} /> {isEs ? 'EN' : 'ES'}
      </button>
    </div>
  )
}

// ── Helper: build URL preserving current language ──
export function langUrl(path: string, lang: string): string {
  return `${path}${path.includes('?') ? '&' : '?'}lang=${lang}`
}

// ── Auth: true when Clerk is configured ──────────
const hasClerk = !!import.meta.env.VITE_CLERK_PUBLISHABLE_KEY

// Sync Clerk's signed-in user into Sentry so every captured error is
// tagged with the user that hit it. Does nothing when Clerk is not
// configured (trial mode) — useUser() must only be called inside a
// ClerkProvider tree.
function SentryUserProvider() {
  if (!hasClerk) return null
  const { isLoaded, isSignedIn, user } = useUser()
  useEffect(() => {
    if (!isLoaded) return
    if (isSignedIn && user) {
      Sentry.setUser({
        id: user.id,
        email: user.primaryEmailAddress?.emailAddress,
      })
    } else {
      Sentry.setUser(null)
    }
  }, [isLoaded, isSignedIn, user])
  return null
}

// TASK-WEB-8 / TASK-WEB-35 — owner-only lock for the takeoff read-only display
// route (owner designation exposure-access-owner-only, TAKEOFF repo commit
// 10aa3d9). The route is now a REAL owner-facing feature (FASE 3 closed), so the
// OWNER lock alone governs access — the legacy dev-mode gate (?dev=1) was
// removed in TASK-WEB-35 as dev-era residue: it forced the owner to append
// ?dev=1 on the first visit even though the sidebar entry was already shown,
// producing a dead link. No identity (trial mode / signed out) still means NO
// access — nobody but the owner sees this route, even logged in. Split in two
// components so the Clerk hook is never called outside a ClerkProvider tree
// (same pattern as SentryUserProvider above). NOTE: the genuinely dev-only
// surfaces (takeoff-diagnostic / ai-draft-takeoff / project-material-list-
// preview) KEEP their ?dev=1 gate — only this owner route dropped it.
function OwnerGatedTakeoffRoute() {
  if (!hasClerk) return <Navigate to=".." replace />
  return <OwnerGatedTakeoffRouteInner />
}

function OwnerGatedTakeoffRouteInner() {
  const { isLoaded, user } = useUser()
  if (!isLoaded) return null
  // TASK-WEB-13: the gate reads Clerk publicMetadata (server-side-writable
  // only) — no owner emails live in the client bundle anymore.
  if (!isTakeoffOwnerAccount(user?.publicMetadata)) {
    return <Navigate to=".." replace />
  }
  return <TakeoffReadonlyDisplayShell />
}

// TASK-WEB-13: same data-layer gate, consumable inside layout components — the
// owner-only sidebar entry only EXISTS in the nav model when this returns true.
// hasClerk is a build-time constant, so the early return keeps hook order
// stable (same established pattern as SentryUserProvider above).
function useIsTakeoffOwner(): boolean {
  if (!hasClerk) return false
  const { isLoaded, user } = useUser()
  if (!isLoaded) return false
  return isTakeoffOwnerAccount(user?.publicMetadata)
}

// Gate that redirects unauthenticated users to /sign-in.
// In trial mode (no Clerk key) children render unconditionally.
function RequireAuth({ children }: { children: React.ReactNode }) {
  if (!hasClerk) return <>{children}</>
  const { isSignedIn, isLoaded } = useAuth()
  if (!isLoaded) return <div className="flex items-center justify-center h-screen"><div className="h-8 w-8 border-2 border-[#06539B] border-t-transparent rounded-full animate-spin" /></div>
  if (!isSignedIn) return <Navigate to="/sign-in" replace />
  // PR2: every authenticated screen shows the read-only bar when the account
  // has no paid subscription (server-enforced; the bar only explains it).
  return <>{children}<ReadOnlyBanner /></>
}

// ── APP ───────────────────────────────────────────

export default function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <LangSync />
          <SentryUserProvider />
          <Routes>
            {/* Public routes — landing page (marketing, ads) */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/contracts/:contractId/sign" element={<ContractSignPage />} />
            {/* Invitación a equipo: pública; la página pide iniciar sesión si hace falta. */}
            <Route path="/invite/:token" element={<InviteAcceptPage />} />

            {/* Auth routes */}
            <Route path="/sign-in/*" element={<AuthPage><SignIn routing="path" path="/sign-in" signUpUrl="/sign-up" /></AuthPage>} />
            <Route path="/sign-up/*" element={<AuthPage><SignUp routing="path" path="/sign-up" signInUrl="/sign-in" /></AuthPage>} />

            {/* Protected app routes */}
            <Route path="/projects" element={<RequireAuth><ProjectList /></RequireAuth>} />
            <Route path="/subscription" element={<RequireAuth><SubscriptionModule /></RequireAuth>} />
            <Route path="/team" element={<RequireAuth><TeamModule /></RequireAuth>} />
            <Route path="/suppliers" element={<RequireAuth><SuppliersModule /></RequireAuth>} />
            <Route path="/account-blocked" element={<RequireAuth><AccountBlockedScreen /></RequireAuth>} />
            <Route path="/projects/:projectId/*" element={<RequireAuth><ProjectLayout /></RequireAuth>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <PWAInstallPrompt />
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  )
}

// ── Centered layout for sign-in / sign-up pages ──
function AuthPage({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-[#FAFAF8] flex flex-col items-center justify-center px-4">
      <div className="mb-6 flex items-center gap-2">
        <img src="/natami_icon.png" alt="NATAMI" className="w-[32px] h-[32px] object-contain rounded-lg" />
        <img src="/natami_logo.png" alt="NATAMI" className="h-[24px] object-contain" />
      </div>
      {children}
    </div>
  )
}

// ── Syncs ?lang= param to i18n on page load ───────
// Handles marketing ad links: if someone clicks a Spanish ad
// (?lang=es), the entire app opens in Spanish.
// If they click an English ad (?lang=en), it opens in English.
// The choice persists in localStorage for return visits.
//
// INTENTIONAL DIVERGENCE FROM FIXES-MASTER spec:
// LangSync reads ?lang= from URL to seed initial language for marketing ad
// campaigns (Phase C Instagram bilingual organic + future paid). The spec
// said to remove this, but breaking ad-driven Spanish landing flows is
// worse than the cleanliness gain. Decision logged 2026-04-11 by Blu.
// If Phase C is sunset, revisit and remove both LangSync AND src/utils/langLinks.ts.
function LangSync() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { i18n } = useTranslation()

  useEffect(() => {
    const langParam = searchParams.get('lang')
    if (langParam && ['en', 'es'].includes(langParam) && langParam !== i18n.language) {
      i18n.changeLanguage(langParam)
      // Store in localStorage so subsequent page navigations remember
      localStorage.setItem('bluaiq-lang', langParam)
    }
  }, [searchParams, i18n])

  return null
}

// ── CALENDAR PAGE (Real Calendar + Sync-to-phone tabs) ────
// Default tab = the real Month/Week/Day CalendarModule (Bloque 3).
// Second tab = the .ics / Google Calendar / Apple Calendar sync feed.
// The old "Gantt tab" was removed — Gantt lives at /gantt as its own module.
function CalendarPage() {
  const { i18n } = useTranslation()
  const isEs = i18n.language === 'es'
  const [tab, setTab] = useState<'calendar' | 'sync'>('calendar')

  return (
    <div>
      <div className="flex gap-1 px-5 pt-5 pb-0">
        <button
          onClick={() => setTab('calendar')}
          className={`px-4 py-2 rounded-t-lg text-[11px] font-[800] transition-colors ${
            tab === 'calendar' ? 'bg-[#06539B] text-white' : 'bg-[#F2F0EB] text-[#9E9890] hover:bg-[#ECEAE4]'
          }`}
        >
          <CalendarDays size={13} className="inline mr-1.5" />
          {isEs ? 'Calendario' : 'Calendar'}
        </button>
        <button
          onClick={() => setTab('sync')}
          className={`px-4 py-2 rounded-t-lg text-[11px] font-[800] transition-colors ${
            tab === 'sync' ? 'bg-[#06539B] text-white' : 'bg-[#F2F0EB] text-[#9E9890] hover:bg-[#ECEAE4]'
          }`}
        >
          <CalendarDays size={13} className="inline mr-1.5" />
          {isEs ? 'Sincronizar al Teléfono' : 'Sync to Phone'}
        </button>
      </div>
      {tab === 'calendar' ? <CalendarModule /> : <CalendarSyncModule />}
    </div>
  )
}

// ── PROJECT LAYOUT (sidebar + content) ────────────

function ProjectLayout() {
  const { projectId } = useParams()
  const { t, i18n } = useTranslation()
  const { getToken } = useAuth()
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const isEs = i18n.language === 'es'

  // PR-NEW: shared project context. Used by ProjectContextHeader so
  // every module page shows which project + whether it is demo + the
  // current module label. The fetch only fires for real projects.
  const projectIsDemo = isDemoProject(projectId)
  const { data: realProjectMeta } = useQuery<{ name?: string | null } | null>({
    queryKey: ['project-meta', projectId],
    enabled: !!projectId && !projectIsDemo,
    queryFn: async () => {
      const token = await getToken()
      const res = await apiFetch(`/api/projects/${projectId}`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      })
      if (!res.ok) return null
      return res.json()
    },
    retry: 0,
    staleTime: 60_000,
  })
  // Demo name comes from FE-only constants; no fake DB project is created.
  const demoProjectMeta = projectIsDemo
    ? DEMO_PROJECTS.find(p => p.id === projectId) ?? null
    : null
  const projectName: string | null =
    (projectIsDemo ? demoProjectMeta?.name : realProjectMeta?.name) ?? null

  // Plan gating is server-authoritative in production: the hook reads
  // GET /api/subscription (Clerk-authenticated) and falls back to
  // STARTER while loading or on error — NEVER to PRO. The demo-plan
  // localStorage toggle (bluaiq_demo_plan / constructai_demo_plan) is
  // honored only when `isDemoMode()` is true.
  const { effectivePlan, isDemo, cycleDemoPlan, subscriptionStatus, plan: storedPlan, accessMode } = useSubscriptionPlan()
  // Codex PR2 [P1]: en solo lectura la cuenta sigue VIENDO los módulos del plan
  // que pagó (recibos, AIA, etc.). Escribir lo bloquea el servidor (402) y el
  // banner lo explica. Fuera de solo lectura, manda el plan efectivo.
  const PLAN_LEVEL: Record<string, number> = { STARTER: 0, PRO: 1, BUILDER: 2 }
  const gatePlan = accessMode === 'read_only' && storedPlan && (PLAN_LEVEL[storedPlan] ?? 0) > (PLAN_LEVEL[effectivePlan] ?? 0)
    ? storedPlan
    : effectivePlan

  // PAST_DUE banner is dismissible per-tab. The server is the source of
  // truth for the status; dismissal only hides the banner, it does not
  // change the underlying subscription state.
  const [pastDueDismissed, setPastDueDismissed] = useState(false)
  const showPastDueBanner = subscriptionStatus === 'PAST_DUE' && !pastDueDismissed

  // PRO-only modules
  const PRO_MODULES = ['finishes', 'receipts', 'emails', 'bids', 'aia-billing']
  // BUILDER-only modules
  const BUILDER_MODULES: string[] = []

  const toggleLang = () => {
    const newLang = isEs ? 'en' : 'es'
    i18n.changeLanguage(newLang)
    localStorage.setItem('bluaiq-lang', newLang)
    // Update URL param so sharing links preserves language
    const url = new URL(window.location.href)
    url.searchParams.set('lang', newLang)
    window.history.replaceState({}, '', url.toString())
  }

  // PR — Sidebar 7-group builder workflow: navigation is reorganized
  // into seven workflow-ordered groups that match how residential /
  // spec-home builders actually think about a project (design →
  // preconstruction → control → procurement → contracts/payments →
  // client → intelligence). Replaces the PR #129 6-group structure.
  // The plan + acceptance criteria live in `docs/BLUAIQ_WEBSITE/
  // NEXT_TASK.md` and the canonical labels are pinned in
  // `docs/BLUAIQ_WEBSITE/terminology.md §0`.
  //
  // bluaIQ should feel like ONE organized builder workflow — not 28
  // disconnected modules. Group headings render as small uppercase
  // bilingual labels above each block in the sidebar. The group keys
  // are stable IDs (English short forms) so tests can pin them.
  //
  // P0 trust cleanup (fix/website-trust-cleanup): removed from sidebar
  // navGroups because their backends are not production-ready:
  //   - `project-packs`     (ComingSoonPlaceholder — no backend)
  //   - `specifications`    (ComingSoonPlaceholder — no backend)
  //   - `permits`           → REAL module since B.5 (PR #503) — placeholder retired
  //   - `client-approvals`  (ComingSoonPlaceholder — no backend)
  //   - `portal-config`     REMOVED 2026-10-21 (Blu): Client Portal is not
  //                          sold; route + public /portal/:projectId removed,
  //                          server endpoints disabled unless PORTAL_ENABLED=true.
  // Routes are preserved so direct URLs and deep links don't 404.
  // Re-add to navGroups when backend + UX are production-ready.
  //
  // Existing route `change-orders` (ChangeOrderModule already exists)
  // is surfaced in the sidebar under Control del Proyecto. No module
  // code changed.
  //
  // Hidden from the sidebar but still routed:
  //   - `finishes` (FinishSelectionsWizard) — pure demo shell
  //     (0 /api/ calls). Real selections live in `selections`
  //     (SelectionBoardModule). Route kept so old deep-links don't
  //     404. Decision preserved from PR #129.
  //   - `rfis` — route exists but is not surfaced; left hidden until
  //     i18n + UX are ready.
  //   - `integrations` (IntegrationsModule) — beta-readiness: provider sync /
  //     QuickBooks are future architecture only and the module shows a
  //     "Coming soon" state, so it must not be visible to real users
  //     (Visible = Functional). Route + module + the in-module dev preview
  //     (?dev=1) are preserved for internal/dev use; only the nav entry is
  //     removed. Re-add when integrations are production-ready.
  type NavItem = {
    path: string
    icon: typeof LayoutDashboard
    label: string
    plan: 'PRO' | 'BUILDER' | null
    /**
     * Sidebar mobile-UX-polish PR: when `featured: true`, the item's
     * icon renders in the brand accent color even when inactive, so
     * primary planning entry points (e.g. Project Packs / Diseños)
     * visually read as such without redesigning the whole sidebar.
     * Label text and active-state styling are unchanged. Optional —
     * defaults to false.
     */
    featured?: boolean
  }
  type NavGroup = { key: string; en: string; es: string; items: NavItem[] }
  // TASK-WEB-13: owner-only nav visibility — same Clerk publicMetadata gate as
  // the route itself, decided in the DATA layer (the item is simply absent from
  // the nav model for everyone else, never CSS-hidden).
  const isTakeoffOwner = useIsTakeoffOwner()
  // Cuantificación switch (Blu 2026-10-21): hidden for customers unless
  // VITE_TAKEOFF_PUBLIC_ENABLED=true; owner always sees it. Reversible —
  // nothing is deleted. See takeoff-diagnostic-v3/takeoffAvailability.ts.
  const takeoffAvailable = isTakeoffAvailable(isTakeoffOwner)
  const navGroups: NavGroup[] = [
    {
      key: 'home',
      en: 'Home',
      es: 'Inicio',
      items: [
        { path: '', icon: LayoutDashboard, label: t('Dashboard'), plan: null },
      ],
    },
    {
      key: 'designs-preconstruction',
      en: 'Designs & Preconstruction',
      es: 'Diseños y Preconstrucción',
      items: [
        { path: 'markup',   icon: PenTool,    label: t('Plans & Markups'),  plan: null },
        ...(takeoffAvailable
          ? [{ path: 'takeoff', icon: FileText, label: t('Quantity Takeoff'), plan: null } as NavItem]
          : []),
        // TASK-WEB-13 (post-closeout backlog, designation exposure-access-owner-only):
        // owner-only entry to the read-only takeoff view — exists in the nav model
        // ONLY when the publicMetadata gate passes, so the owner reaches it without
        // typing URLs. Same label in both languages (proper name of the new view).
        ...(isTakeoffOwner
          ? [{ path: 'takeoff-readonly-display', icon: FileText, label: 'Takeoff (vista nueva)', plan: null } as NavItem]
          : []),
        { path: 'quotes',   icon: DollarSign, label: t('Quotes & Pricing'), plan: null },
        { path: 'bids',     icon: Hammer,     label: t('Contractor Bids'),  plan: 'PRO' },
      ],
    },
    {
      key: 'project-control',
      en: 'Project Control',
      es: 'Control del Proyecto',
      items: [
        { path: 'budget',        icon: BarChart3,     label: t('Budget'),          plan: null },
        { path: 'gantt',         icon: GanttChart,    label: t('Gantt Schedule'),  plan: null },
        { path: 'calendar',      icon: CalendarDays,  label: t('Calendar'),        plan: null },
        { path: 'daily-logs',    icon: ClipboardList, label: t('Daily Log'),       plan: null },
        { path: 'photos',        icon: Camera,        label: t('Progress Photos'), plan: null },
        { path: 'permits',       icon: FileCheck2,    label: t('Permits'),         plan: null },
        { path: 'change-orders', icon: RefreshCw,     label: t('Change Orders'),   plan: null },
        // Punch list — same label in ES and EN (the term is used as-is on Texas job sites). All plans.
        { path: 'punch-list',    icon: ListChecks,    label: 'Punch list',         plan: null },
      ],
    },
    {
      key: 'procurement-field',
      en: 'Procurement & Field',
      es: 'Compras y Obra',
      items: [
        ...(isTakeoffOwner
          ? [{ path: 'purchase-candidates', icon: PackageCheck, label: isEs ? 'Compra candidata' : 'Purchase Candidate', plan: null } as NavItem]
          : []),
        { path: 'materials', icon: Truck,      label: t('Material Orders'),     plan: 'PRO' },
        { path: 'receipts',  icon: Receipt,    label: t('Receipts & Expenses'), plan: 'PRO' },
        { path: 'emails',    icon: Mail,       label: t('Project Emails'),      plan: 'PRO' },
        { path: 'contacts',  icon: Users,      label: t('Contacts'),            plan: null },
        { path: 'documents', icon: FolderOpen, label: t('Documents'),           plan: null },
      ],
    },
    {
      key: 'contracts-compliance-payments',
      en: 'Contracts, Compliance & Payments',
      // Shorter ES label: the full "Contratos, Cumplimiento y Pagos" wrapped to
      // two lines in the sidebar (the group-header span has no nowrap), making
      // this group look centered/taller than the others. "Compliance" lives in
      // the module content; the sidebar label stays concise and single-line.
      es: 'Contratos y Pagos',
      items: [
        { path: 'contracts',   icon: ScrollText,      label: t('Contracts'),              plan: null },
        { path: 'insurance',   icon: Shield,          label: t('Insurance & Compliance'), plan: null },
        { path: 'payments',    icon: CreditCard,      label: t('Payments'),               plan: null },
        { path: 'aia-billing', icon: FileSpreadsheet, label: t('Progress Billing'),       plan: 'PRO' },
      ],
    },
    {
      key: 'client-selections',
      en: 'Client & Selections',
      es: 'Cliente y Selecciones',
      items: [
        { path: 'selections', icon: Palette, label: t('Selections Board'), plan: null },
      ],
    },
    {
      key: 'intelligence-settings',
      en: 'Intelligence & Settings',
      es: 'Inteligencia y Configuración',
      items: [
        { path: 'insights',     icon: Brain,         label: t('AI Analysis'),                          plan: 'BUILDER' },
        { path: 'chat',         icon: MessageCircle, label: t('Project Chat'),                         plan: null },
        { path: 'subscription', icon: CreditCard,    label: t('Plan & Billing'),                       plan: null },
        { path: 'team',         icon: Users,         label: isEs ? 'Equipo' : 'Team',                  plan: null },
      ],
    },
  ]
  // Flat view kept for back-compat where any downstream logic
  // iterates a single list (mobile drawer, future tests, etc.).
  const navItems: NavItem[] = navGroups.flatMap((g) => g.items)

  // PR-NEW: derive the current-module label by matching the route
  // sub-path against the registered nav items. We strip the
  // `/projects/:projectId/` prefix and take the first remaining
  // segment. Empty segment → Dashboard (path '' in navGroups).
  // Unknown segments (e.g. ad-hoc routes not in navGroups) → null,
  // which makes ProjectContextHeader omit the module-label line
  // rather than render a wrong label.
  const sub = (() => {
    const after = location.pathname.replace(/^\/projects\/[^/]+\/?/, '')
    return after.split('/')[0] || ''
  })()
  const currentNavItem = navItems.find((it) => it.path === sub) || null
  const moduleLabel = currentNavItem?.label ?? null

  // ── Sidebar accordion Phase 2 ────────────────────────────────────
  // currentGroupKey: which nav group owns the active route.
  // Falls back to 'home' for unmatched sub-paths (e.g. ad-hoc routes).
  const currentGroupKey =
    navGroups.find((g) => g.items.some((i) => i.path === sub))?.key ?? 'home'

  // openGroups: set of group keys the user has explicitly expanded.
  // Active group + home are always open at render time via isGroupOpen(),
  // regardless of this set — no useEffect required.
  // Persisted to localStorage('bluaiq.sidebar.openGroups') as a JSON array.
  // All localStorage calls are wrapped in try/catch so a SecurityError or
  // quota error in an embedded/private-browsing context never crashes the
  // sidebar.
  const [openGroups, setOpenGroups] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem('bluaiq.sidebar.openGroups')
      if (raw) return new Set<string>(JSON.parse(raw) as string[])
    } catch {}
    return new Set<string>()
  })

  // isGroupOpen: a group is open if it is home, the active group,
  // or the user has explicitly expanded it this session.
  const isGroupOpen = (key: string) =>
    key === 'home' || key === currentGroupKey || openGroups.has(key)

  // toggleGroup: expand or collapse a non-home, non-active group.
  // No-ops for home and currentGroupKey — both must always stay visible.
  const toggleGroup = (key: string) => {
    if (key === 'home' || key === currentGroupKey) return
    setOpenGroups((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      try {
        localStorage.setItem('bluaiq.sidebar.openGroups', JSON.stringify(Array.from(next)))
      } catch {}
      return next
    })
  }

  // Sidebar Contacts NavLink — Material Orders context echo.
  // When the user is currently on Material Orders (`sub` is either
  // `materials` or its `material-orders` alias — see App.tsx route
  // table) and clicks the sidebar "Contacts" link, route to
  // `/projects/:id/contacts?from=material-orders` so the existing
  // gate in ContactsModule (PR #173) fires and the blank-page-
  // capable Email / SMS / WhatsApp pills are replaced with the
  // bilingual back-to-MO helper. From any other module, sidebar
  // Contacts stays at `/projects/:id/contacts` with no query
  // param — no behavior change. Only the `contacts` path is
  // re-routed; every other sidebar item is unaffected.
  const buildSidebarNavTo = (itemPath: string): string => {
    const isMaterialOrdersContext = sub === 'materials' || sub === 'material-orders'
    const base = `/projects/${projectId}/${itemPath}`
    if (itemPath === 'contacts' && isMaterialOrdersContext) {
      return `${base}?from=material-orders`
    }
    return base
  }

  return (
    <div className="flex h-screen bg-[#FAFAF8]">
      {/* Sidebar — desktop */}
      <aside className="hidden lg:flex flex-col w-[240px] bg-white border-r border-[#ECEAE4] flex-shrink-0">
        {/* Header — small back link above compact wordmark.
            Sidebar mobile-UX-polish PR: tighter logo footprint, with a
            max-height cap so the wordmark cannot dominate the rail. */}
        <div className="px-4 pt-2.5 pb-2.5 border-b border-[#ECEAE4]">
          <NavLink to="/projects" className="flex items-center gap-1 text-[10px] font-[700] text-[#9E9890] hover:text-[#06539B] transition-colors mb-1.5">
            <ChevronLeft size={12} />
            {t('All Projects')}
          </NavLink>
          <img src="/natami_logo.png" alt="NATAMI" className="w-full max-w-[150px] max-h-[32px] h-auto object-contain" />
        </div>

        {/* Nav items — grouped by builder workflow. Sidebar
            mobile-UX-polish PR: hairline divider above each group
            except the first; group labels bumped to 10px in a darker
            shade for legibility; `overscroll-contain` so the body
            doesn't bounce when the nav scrolls. */}
        <nav
          className="flex-1 py-1 overflow-y-auto overscroll-contain"
          aria-label={isEs ? 'Navegación del proyecto' : 'Project navigation'}
        >
          {navGroups.map((group, gIdx) => (
            <div
              key={group.key}
              role="group"
              aria-label={isEs ? group.es : group.en}
              className={`pb-0.5 ${gIdx > 0 ? 'pt-2 border-t border-[#F4F2EE]' : ''}`}
            >
              {/* sidebar-accordion-phase2: home group is always open — plain
                  div heading, no chevron, no toggle. Every other group is a
                  button that expands/collapses via toggleGroup(). */}
              {group.key === 'home' ? (
                <div
                  data-testid={`sidebar-group-${group.key}`}
                  className="px-4 mt-2 mb-1 text-[10px] font-[800] uppercase tracking-[0.5px] text-[#7C7670] truncate"
                >
                  {isEs ? group.es : group.en}
                </div>
              ) : (
                <button
                  data-testid={`sidebar-group-${group.key}`}
                  onClick={() => toggleGroup(group.key)}
                  aria-expanded={isGroupOpen(group.key)}
                  className="w-full flex items-center justify-between px-4 mt-2 mb-1 text-[10px] font-[800] uppercase tracking-[0.5px] text-[#7C7670] hover:text-[#1C1A17] transition-colors"
                >
                  <span className="flex-1 min-w-0 truncate text-left">{isEs ? group.es : group.en}</span>
                  <ChevronDown
                    size={10}
                    className={`flex-shrink-0 transition-transform duration-150 ${
                      isGroupOpen(group.key) ? '' : '-rotate-90'
                    }`}
                  />
                </button>
              )}
              {isGroupOpen(group.key) && group.items.map((item) => {
                const needsBadge = item.plan && effectivePlan === 'STARTER'
                return (
                  <NavLink
                    key={item.path || 'dashboard'}
                    to={buildSidebarNavTo(item.path)}
                    end={item.path === ''}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 px-4 py-[9px] mx-2 rounded-lg text-[12px] transition-colors ${
                        isActive
                          ? 'bg-[#06539B] text-white font-[700] shadow-sm'
                          : 'text-[#403E3A] font-[600] hover:bg-[#F2F0EB] hover:text-[#1C1A17]'
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <item.icon
                          size={16}
                          className={
                            !isActive && item.featured ? 'text-[#06539B]' : undefined
                          }
                        />
                        <span className="flex-1">{item.label}</span>
                        {needsBadge && <ProBadge plan={item.plan as 'PRO' | 'BUILDER'} />}
                      </>
                    )}
                  </NavLink>
                )
              })}
            </div>
          ))}
        </nav>

        {/* Footer — Sidebar mobile-UX-polish PR: tightened spacing,
            "Subscription" label aligned with the main nav by using the
            same `t('Plan & Billing')` translation key (matches
            terminology.md §2). */}
        <div className="px-4 py-2.5 border-t border-[#ECEAE4] space-y-1.5">
          <NavLink to={`/projects/${projectId}/subscription`}
            className="flex items-center gap-2 text-[10px] font-[700] text-[#06539B] hover:underline">
            <Crown size={12} /> {t('Plan & Billing')}
          </NavLink>
          <button onClick={toggleLang}
            className="flex items-center gap-2 text-[12px] font-[700] text-[#9E9890] hover:text-[#1C1A17]">
            <Globe size={12} /> {isEs ? 'English' : 'Español'}
          </button>
          {/* Account menu — sign out / switch account.
              UserButton is the official Clerk component: shows the
              user's avatar, opens a branded modal with sign-out and
              profile options. Guarded by hasClerk so trial mode
              (no ClerkProvider) never renders it. afterSignOutUrl
              returns the user to the landing page. */}
          {hasClerk && <UserButton afterSignOutUrl="/" />}
          {/* Demo plan toggle (only in demo mode — the hook scopes
              cycleDemoPlan to demo mode internally so localStorage is
              never the source of truth in production). */}
          {isDemo && (
          <button onClick={cycleDemoPlan}
            className={`flex items-center gap-2 text-[10px] font-[800] px-2 py-1 rounded-full transition-colors ${
              effectivePlan === 'STARTER' ? 'bg-gray-100 text-gray-600' :
              effectivePlan === 'PRO' ? 'bg-[#06539B]/10 text-[#06539B]' :
              'bg-amber-100 text-amber-700'
            }`}>
            <Crown size={10} /> Demo: {effectivePlan}
          </button>
          )}
        </div>
      </aside>

      {/* Demo banner */}
      {isDemo && (
      <div className="bg-[#06539B] text-white text-center py-1.5 px-4 flex items-center justify-center gap-3 fixed top-0 inset-x-0 z-[60]">
        <span className="text-[10px] font-[800] uppercase tracking-[0.5px]">
          {isEs ? 'Modo demo' : 'Demo mode'}
        </span>
        <NavLink to="/" className="text-[10px] font-[800] bg-white/20 px-3 py-0.5 rounded-full hover:bg-white/30 transition-colors">
          ← {isEs ? 'Volver al sitio' : 'Back to website'}
        </NavLink>
      </div>
      )}

      {/* Mobile header — F1 shell fix + follow-up spacing bump.
          The demo banner above is conditional on `isDemo`, so the
          mobile header's `top` MUST match: anchor to the viewport
          top in real projects (no banner), and offset by the banner
          in demo so the two surfaces stack cleanly. The original
          F1 fix used a 28px offset (basis: the demo banner's
          `py-1.5` padding only). In practice the inner pill
          (`py-0.5 rounded-full`) pushes the banner's rendered
          height to ~30px, so the offset was bumped from 28px →
          32px to give a 2px cushion below the banner's bottom
          edge and avoid the banner↔header micro-overlap visible
          on production mobile screenshots. */}
      <AuthenticatedMobileHeader
        isDemo={isDemo}
        isEs={isEs}
        onMenu={() => setSidebarOpen(true)}
        onToggleLang={toggleLang}
      />

      {/* Mobile sidebar overlay — Sidebar mobile-UX-polish PR: tighter
          header (smaller logo + reduced padding), tighter item spacing,
          hairline group dividers, darker inactive text for contrast,
          `overscroll-contain` on the scroll surface, "Subscription"
          label aligned with the main nav via `t('Plan & Billing')`. */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/30" onClick={() => setSidebarOpen(false)} />
          <div className="relative w-[280px] bg-white flex flex-col shadow-xl">
            {/* Header — compact wordmark + close, capped height. */}
            <div className="px-4 pt-2 pb-2 border-b border-[#ECEAE4]">
              <div className="flex items-center justify-between mb-1.5">
                <NavLink
                  to="/projects"
                  onClick={() => setSidebarOpen(false)}
                  className="flex items-center gap-1 text-[10px] font-[700] text-[#9E9890] hover:text-[#06539B] transition-colors"
                >
                  <ChevronLeft size={12} />
                  {t('All Projects')}
                </NavLink>
                <button onClick={() => setSidebarOpen(false)} className="text-[#9E9890] hover:text-[#1C1A17] transition-colors">
                  <X size={18} />
                </button>
              </div>
              <img src="/natami_logo.png" alt="NATAMI" className="w-full max-w-[140px] max-h-[28px] h-auto object-contain" />
            </div>
            <nav
              className="flex-1 py-1 overflow-y-auto overscroll-contain"
              aria-label={isEs ? 'Navegación del proyecto' : 'Project navigation'}
            >
              {navGroups.map((group, gIdx) => (
                <div
                  key={group.key}
                  role="group"
                  aria-label={isEs ? group.es : group.en}
                  className={`pb-0.5 ${gIdx > 0 ? 'pt-2 border-t border-[#F4F2EE]' : ''}`}
                >
                  {/* sidebar-accordion-phase2: same home/collapsible split as
                      the desktop sidebar. toggleGroup closes other groups but
                      never collapses home or the active group. */}
                  {group.key === 'home' ? (
                    <div
                      data-testid={`sidebar-group-${group.key}`}
                      className="px-4 mt-2 mb-1 text-[10px] font-[800] uppercase tracking-[0.5px] text-[#7C7670] truncate"
                    >
                      {isEs ? group.es : group.en}
                    </div>
                  ) : (
                    <button
                      data-testid={`sidebar-group-${group.key}`}
                      onClick={() => toggleGroup(group.key)}
                      aria-expanded={isGroupOpen(group.key)}
                      className="w-full flex items-center justify-between px-4 mt-2 mb-1 text-[10px] font-[800] uppercase tracking-[0.5px] text-[#7C7670] hover:text-[#1C1A17] transition-colors"
                    >
                      <span className="flex-1 min-w-0 truncate text-left">{isEs ? group.es : group.en}</span>
                      <ChevronDown
                        size={10}
                        className={`flex-shrink-0 transition-transform duration-150 ${
                          isGroupOpen(group.key) ? '' : '-rotate-90'
                        }`}
                      />
                    </button>
                  )}
                  {isGroupOpen(group.key) && group.items.map((item) => (
                    <NavLink
                      key={item.path || 'dashboard'}
                      to={buildSidebarNavTo(item.path)}
                      end={item.path === ''}
                      onClick={() => setSidebarOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center gap-2.5 px-4 py-[9px] mx-2 rounded-lg text-[13px] transition-colors ${
                          isActive
                            ? 'bg-[#06539B] text-white font-[700] shadow-sm'
                            : 'text-[#403E3A] font-[600] hover:bg-[#F2F0EB]'
                        }`
                      }
                    >
                      {({ isActive }) => (
                        <>
                          <item.icon
                            size={16}
                            className={
                              !isActive && item.featured ? 'text-[#06539B]' : undefined
                            }
                          />
                          {item.label}
                        </>
                      )}
                    </NavLink>
                  ))}
                </div>
              ))}
            </nav>
            <div className="px-4 py-2.5 border-t border-[#ECEAE4] space-y-1.5">
              <NavLink to={`/projects/${projectId}/subscription`} onClick={() => setSidebarOpen(false)}
                className="flex items-center gap-2 text-[11px] font-[700] text-[#06539B]">
                <Crown size={12} /> {t('Plan & Billing')}
              </NavLink>
              <button onClick={toggleLang}
                className="flex items-center gap-2 text-[11px] font-[700] text-[#9E9890]">
                <Globe size={12} /> {isEs ? 'English' : 'Español'}
              </button>
              {/* Account menu — sign out / switch account (mobile drawer). */}
              {hasClerk && <UserButton afterSignOutUrl="/" />}
            </div>
          </div>
        </div>
      )}

      {/* Main content — F1 shell fix + follow-up spacing bump.
          Mobile padding-top MUST clear whatever fixed surfaces sit
          above the scroll area. Real measured heights:
            • demo banner ≈ 30px (py-1.5 + inner pill `py-0.5`)
            • mobile header ≈ 53px (py-3 + p-1 icon-button tap target 28 + border 1)
          So the demo stack is ≈ 83px and the non-demo header alone
          is ≈ 53px. The original F1 fix used pt-[80px] / pt-[56px]
          which produced 0-3px of cushion in practice — visible on
          production mobile screenshots as ProjectContextHeader
          clipping under the fixed mobile header, especially with
          long project names, the "Solo lectura / Read-only" badge,
          and Dashboard/Gantt taller top context. Bumped to
          pt-[88px] (demo) / pt-[64px] (non-demo) to restore the
          intended ~5-11px cushion. Desktop is unchanged via
          `lg:pt-0`. */}
      <main className={`flex-1 overflow-y-auto lg:pt-0 ${isDemo ? 'pt-[88px]' : 'pt-[64px]'}`}>
        {/* PR-NEW: shared project context header. Lives at the
            shell level so every module page surfaces project +
            demo state + current-module label without each module
            having to re-implement it. Pure presentational; no
            business logic, no network calls of its own. */}
        <ProjectContextHeader
          isDemo={projectIsDemo}
          isEs={isEs}
          projectName={projectName}
          projectId={projectId}
          moduleLabel={moduleLabel}
        />
        {showPastDueBanner && (
          <PaymentWarningBanner
            // daysLeft is intentionally omitted here — real grace-period
            // timing (paymentFailedAt → graceEndsAt) is not yet stored
            // in the backend. Passing no value shows an honest no-count
            // message instead of the previously hardcoded 7. Wire up
            // `paymentGraceDaysLeft` from useSubscriptionPlan once
            // /api/subscription exposes it (Option B follow-up).
            onUpdatePayment={() => setPastDueDismissed(true)}
            onDismiss={() => setPastDueDismissed(true)}
          />
        )}
        {/* fix(project-control): key resets ErrorBoundary on every module
            navigation so a crash in one module (e.g. Gantt null-date throw)
            cannot leave the boundary stuck in hasError:true and block every
            other module for the rest of the session. `sub` is the first
            URL segment after /projects/:id/ — empty string for Dashboard,
            the route name otherwise. React mounts a fresh ErrorBoundary
            instance whenever sub changes, scoping errors to the module
            that threw them. */}
        <ErrorBoundary key={sub}>
        <TakeoffAvailabilityContext.Provider value={takeoffAvailable}>
        <Routes>
          {/* Available to ALL plans */}
          <Route index element={<Dashboard />} />
          {/* PR #138A: Takeoff demo guard.
              TakeoffModule crashes on demo data ("Algo salió mal" via
              `cat.normalize('NFD')` on an undefined category). Fixing
              the .normalize call belongs to TAKEOFF internals and is
              out of scope here. Instead, short-circuit the route at
              the website layer: demo projects render the honest
              saved-project notice and never mount TakeoffModule. Real
              projects are unaffected. */}
          <Route
            path="takeoff"
            element={
              !takeoffAvailable
                ? <Navigate to=".." replace />
                : isDemoProject(projectId)
                ? <div className="p-5 max-w-2xl mx-auto"><SavedProjectOnlyNotice testId="takeoff-demo-notice" /></div>
                : <TakeoffModule />
            }
          />
          {/* Developer-only diagnostic surface — never reachable for customers.
              Gated by ?dev=1, localStorage bluaiq_dev_mode, or VITE_TAKEOFF_DIAGNOSTIC_V3_ENABLED.
              Falls back to the legacy takeoff so customers cannot see a 404. */}
          <Route
            path="takeoff-diagnostic"
            element={takeoffAvailable && isDiagnosticV3VisibleToClient() ? <DiagnosticTakeoffModule /> : <Navigate to=".." replace />}
          />
          {/* AI-DRAFT-TAKEOFF-1 — hidden, developer-only demo shell (read-only).
              NOT added to the sidebar navGroups. Reuses the same dev-mode gate as the
              diagnostic surface (?dev=1 / localStorage bluaiq_dev_mode / VITE flag);
              normal customers are redirected to the project root, never a 404. It
              creates nothing, writes nothing, and reaches no backend. */}
          <Route
            path="ai-draft-takeoff"
            element={takeoffAvailable && isDiagnosticV3VisibleToClient() ? <AiDraftTakeoffShell /> : <Navigate to=".." replace />}
          />
          {/* PROJECT-MATERIAL-LIST-1 — hidden, developer-only demo shell (read-only).
              NOT added to the sidebar navGroups. Reuses the same dev-mode gate as the
              diagnostic surface (?dev=1 / localStorage bluaiq_dev_mode / VITE flag);
              normal customers are redirected to the project root, never a 404. It
              creates nothing, writes nothing, reaches no backend, and does not
              connect to any Takeoff implementation. */}
          <Route
            path="project-material-list-preview"
            element={takeoffAvailable && isDiagnosticV3VisibleToClient() ? <ProjectMaterialListDemoShell /> : <Navigate to=".." replace />}
          />
          {/* TASK-WEB-1/8/13/16 — owner-only Takeoff display module. TASK-WEB-35:
              gated SOLELY by the owner-only Clerk publicMetadata lock (the legacy
              ?dev=1 dev-mode gate was removed — this is a real owner-facing
              feature now); everyone else is redirected to the project root, never
              a 404. TASK-WEB-16: the real project's flow DOES reach the backend
              (owner-gated server-side): uploads the plan PDF and reads back the
              candidate/read-only quantification payload. It still creates no
              purchase/final/downstream records and calls no AI; the demo/synthetic
              projects remain fully static. */}
          <Route
            path="takeoff-readonly-display"
            element={<OwnerGatedTakeoffRoute />}
          />
          <Route
            path="purchase-candidates"
            element={isTakeoffOwner ? <CertifiedPurchaseCandidateView /> : <Navigate to=".." replace />}
          />
          <Route
            path="purchase-candidates/quote-draft/:packageId"
            element={isTakeoffOwner ? <CertifiedQuoteDraftView /> : <Navigate to=".." replace />}
          />
          <Route
            path="purchase-candidates/selection-drafts/:draftId"
            element={isTakeoffOwner ? <CertifiedSelectionDraftView /> : <Navigate to=".." replace />}
          />
          <Route path="quotes" element={<QuotesModule />} />
          <Route path="rfq" element={<QuotesModule />} />
          <Route path="budget" element={<BudgetModule />} />
          {/* PR — Sidebar 7-group builder workflow: four new sidebar
              entries with lightweight bilingual ComingSoonPlaceholder
              routes. No backend / no schema in this PR — full data
              layer + CRUD lands in focused follow-up PR pairs. */}
          <Route
            path="project-packs"
            element={
              <ComingSoonPlaceholder
                testId="coming-soon-project-packs"
                titleEn="Project Packs"
                titleEs="Diseños"
                descriptionEn="Start a project from a design template. Project Packs will pre-seed base specs, base budget, initial documents, quote templates, the initial schedule, a permit checklist, and the selections starter board."
                descriptionEs="Inicia un proyecto desde una plantilla de diseño. Los Diseños precargarán especificaciones base, presupuesto base, documentos iniciales, plantillas de cotización, el cronograma inicial, una lista de permisos y el tablero inicial de selecciones."
              />
            }
          />
          <Route
            path="specifications"
            element={
              <ComingSoonPlaceholder
                testId="coming-soon-specifications"
                titleEn="Specifications"
                titleEs="Especificaciones"
                descriptionEn="Track product, finish, and assembly specifications for the project — the canonical source for what is being built and at what quality level."
                descriptionEs="Lleva el registro de las especificaciones de producto, acabado y ensamblaje del proyecto — la fuente canónica de qué se construye y con qué nivel de calidad."
              />
            }
          />
          {/* B.5 (PR #503): the placeholder this route served since the P0
              trust cleanup described EXACTLY this module. It has a backend
              now; the real module replaces it. */}
          <Route path="permits" element={<PermitsModule />} />
          <Route
            path="client-approvals"
            element={
              <ComingSoonPlaceholder
                testId="coming-soon-client-approvals"
                titleEn="Client Approvals"
                titleEs="Aprobaciones del Cliente"
                descriptionEn="Capture client sign-off on change orders, selections, and milestones. Approvals are tracked with date, requester, and the underlying decision so the project history stays auditable."
                descriptionEs="Captura la aprobación del cliente sobre órdenes de cambio, selecciones e hitos. Las aprobaciones quedan registradas con fecha, solicitante y la decisión correspondiente para mantener un historial auditable."
              />
            }
          />
          <Route path="contracts" element={<ContractsModule />} />
          <Route path="contracts/new" element={<ContractsModule />} />
          <Route path="payments" element={<PaymentsModule />} />
          <Route path="insurance" element={<InsuranceModule />} />
          <Route path="contacts" element={<ContactsModule />} />
          <Route path="photos" element={<PhotosModule />} />
          <Route path="documents" element={<DocumentsModule />} />
          <Route path="calendar" element={<CalendarPage />} />
          <Route path="insights" element={
            <ProFeatureGate requiredPlan="BUILDER" currentPlan={gatePlan} isEs={isEs}
              featureName="AI Insights" featureNameEs="Inteligencia Artificial"
              description="AI-powered project analysis: cost predictions, risk alerts, schedule optimization, and smart recommendations."
              descriptionEs="Análisis de proyecto con IA: predicciones de costos, alertas de riesgo, optimización de cronograma y recomendaciones inteligentes.">
              <InsightsModule />
            </ProFeatureGate>
          } />
          <Route path="chat" element={<ProjectChatModule />} />
          <Route path="gantt" element={<GanttModule />} />
          <Route path="markup" element={<DrawingMarkup />} />
          <Route path="drawings" element={<DrawingMarkup />} />
          <Route path="change-orders" element={<ChangeOrderModule />} />
          <Route path="rfis" element={<RFIModule />} />
          <Route path="selections" element={<SelectionBoardModule />} />
          <Route path="daily-logs" element={<DailyLogModule />} />
          <Route path="punch-list" element={<PunchListModule />} />
          <Route path="subscription" element={<SubscriptionModule />} />
          <Route path="team" element={<TeamModule />} />

          {/* PRO-only modules — gated for STARTER */}
          <Route path="bids" element={
            <ProFeatureGate requiredPlan="PRO" currentPlan={gatePlan} isEs={isEs}
              featureName="Bid Comparison" featureNameEs="Comparación de Propuestas"
              description="Compare contractor bids side-by-side, detect scope gaps, and auto-score."
              descriptionEs="Compara propuestas lado a lado, detecta vacíos de alcance y califica automáticamente.">
              <BidsModule />
            </ProFeatureGate>
          } />
          <Route path="receipts" element={
            <ProFeatureGate requiredPlan="PRO" currentPlan={gatePlan} isEs={isEs}
              featureName="Expense Sheet" featureNameEs="Hoja de Gastos"
              description="Scan receipts, auto-catalog expenses, and generate summary sheets."
              descriptionEs="Escanea recibos, cataloga gastos automáticamente y genera hojas resumen.">
              <ReceiptsModule />
            </ProFeatureGate>
          } />
          <Route path="emails" element={
            <ProFeatureGate requiredPlan="PRO" currentPlan={gatePlan} isEs={isEs}
              featureName="AI Email Generator" featureNameEs="Generador de Emails con IA"
              description="Generate professional emails for contractors, clients, and inspectors in seconds."
              descriptionEs="Genera emails profesionales para contratistas, clientes e inspectores en segundos.">
              <EmailsModule />
            </ProFeatureGate>
          } />
          <Route path="finishes" element={
            <ProFeatureGate requiredPlan="PRO" currentPlan={gatePlan} isEs={isEs}
              featureName="Finish Selections" featureNameEs="Selección de Acabados"
              description="Select finishes room-by-room with 3 price tiers and auto-add to budget."
              descriptionEs="Selecciona acabados cuarto por cuarto con 3 niveles de precio y agrégalos al presupuesto.">
              <FinishSelectionsWizard />
            </ProFeatureGate>
          } />
          <Route path="materials" element={
            <ProFeatureGate requiredPlan="PRO" currentPlan={gatePlan} isEs={isEs}
              featureName="Material Ordering Schedule" featureNameEs="Programa de Pedidos de Material"
              description="AI tells you WHEN to order each material based on lead times and your project timeline."
              descriptionEs="La IA te dice CUÁNDO pedir cada material basado en tiempos de entrega y tu timeline.">
              <MaterialOrderSchedule />
            </ProFeatureGate>
          } />
          {/* Alias for Calendar's 📦 reminder links (/projects/:id/material-orders?category=X) */}
          <Route path="material-orders" element={
            <ProFeatureGate requiredPlan="PRO" currentPlan={gatePlan} isEs={isEs}
              featureName="Material Ordering Schedule" featureNameEs="Programa de Pedidos de Material"
              description="AI tells you WHEN to order each material based on lead times and your project timeline."
              descriptionEs="La IA te dice CUÁNDO pedir cada material basado en tiempos de entrega y tu timeline.">
              <MaterialOrderSchedule />
            </ProFeatureGate>
          } />
          <Route path="aia-billing" element={
            <ProFeatureGate requiredPlan="PRO" currentPlan={gatePlan} isEs={isEs}
              featureName="Progress Billing" featureNameEs="Facturación de Avance"
              description="Generate AIA-style progress-billing draw requests with auto-calculated retainage and PDF export. For reference only — not an official AIA document; acceptance is at the lender's discretion."
              descriptionEs="Genera solicitudes de pago progresivas estilo AIA con retención auto-calculada y exportación PDF. Solo como referencia — no es un documento oficial AIA; la aceptación queda a discreción del prestamista.">
              <AIABillingModule />
            </ProFeatureGate>
          } />
          <Route path="integrations" element={
            <ProFeatureGate requiredPlan="BUILDER" currentPlan={gatePlan} isEs={isEs}
              featureName="Integrations" featureNameEs="Integraciones"
              description="Connect NATAMI to finance and spend systems like QuickBooks and Ramp."
              descriptionEs="Conecta NATAMI con sistemas de finanzas y spend como QuickBooks y Ramp.">
              <IntegrationsModule />
            </ProFeatureGate>
          } />
        </Routes>
        </TakeoffAvailabilityContext.Provider>
        </ErrorBoundary>
      </main>
    </div>
  )
}
